/**
 * Wrapper around the shared LayoutFlowStatic that provides Redux functionality
 * for the financial-advisory frontend.
 */
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { PaneType } from "@financial-advisory/data-access/api-types";
import { setActivePaneId } from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  LayoutFlowStatic as SharedLayoutFlowStatic,
  LayoutFlowStaticProps as SharedLayoutFlowStaticProps,
} from "@unlikelyai-magic/shared/components";

export interface LayoutFlowStaticProps
  extends Omit<SharedLayoutFlowStaticProps, "onNodeClick" | "onPaneClick"> {
  /**
   * Optional callback when a node is clicked.
   * If not provided, will dispatch setActivePaneId Redux action.
   */
  onNodeClick?: SharedLayoutFlowStaticProps["onNodeClick"];
  /**
   * Optional callback when the pane is clicked.
   * If not provided, will dispatch setActivePaneId(null) Redux action.
   */
  onPaneClick?: SharedLayoutFlowStaticProps["onPaneClick"];
}

/**
 * LayoutFlowStatic with Redux integration for financial-advisory.
 * Uses the shared component but adds Redux dispatching for node/pane clicks.
 */
export const LayoutFlowStatic = ({
  onNodeClick: onNodeClickProp,
  onPaneClick: onPaneClickProp,
  ...props
}: LayoutFlowStaticProps) => {
  const dispatch = useDispatch();

  const handleNodeClick = useCallback(
    (nodeId: string, nodeType: string, paneType?: PaneType) => {
      if (onNodeClickProp) {
        onNodeClickProp(nodeId, nodeType, paneType);
      } else {
        // Default Redux behavior
        if (nodeType === "group") {
          if (paneType === PaneType.DEFINITION) {
            dispatch(setActivePaneId(nodeId));
          } else {
            dispatch(setActivePaneId(null));
          }
        }
      }
    },
    [dispatch, onNodeClickProp]
  );

  const handlePaneClick = useCallback(() => {
    if (onPaneClickProp) {
      onPaneClickProp();
    } else {
      // Default Redux behavior
      dispatch(setActivePaneId(null));
    }
  }, [dispatch, onPaneClickProp]);

  return (
    <SharedLayoutFlowStatic
      {...props}
      onNodeClick={handleNodeClick}
      onPaneClick={handlePaneClick}
    />
  );
};
